import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { PushNotifications } from '@capacitor/push-notifications';
import { api } from '@/api/repository/axios';
import { markPushEntry } from '@/common/analytics';
import { navigationService } from '@/common/navigationService';
import { initDeepLinks } from '@/native/deepLink';
import { setPaymentMode } from '@/native/paymentNavigation';

/**
 * 네이티브 앱(Capacitor)에서만 동작하는 초기화.
 * 웹 브라우저에서는 아무것도 하지 않으므로 main.tsx에서 무조건 호출해도 안전하다.
 */
export function initNativeApp(): void {
    if (!Capacitor.isNativePlatform()) {
        return;
    }

    // 최강 안전장치: 우리 origin 페이지가 (재)로드됐다는 것 자체가 "결제창 밖"이라는 신호.
    // 결제 중 이탈·복귀·성공/실패 모든 경로가 이 초기화를 다시 타므로 결제모드가 항상 해제된다.
    void setPaymentMode(false);

    // 앱 전용 CSS 훅: html.capacitor-app / html.platform-ios|android
    document.documentElement.classList.add('capacitor-app', `platform-${Capacitor.getPlatform()}`);

    // iOS: 입력 포커스 시 자동 확대(폰트 16px 미만)로 화면이 커진 채 안 돌아오는 문제 방지.
    // 앱에서만 줌을 잠근다 (웹 브라우저 접근성에는 영향 없음).
    const viewport = document.querySelector('meta[name="viewport"]');
    viewport?.setAttribute(
        'content',
        'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover',
    );

    // 공유 링크(Universal Link / myungchisa:// 스킴)로 앱이 열렸을 때 해당 화면으로 이동.
    // 라우터가 아직 없으면 deepLink 모듈이 보관했다가 App.tsx 가 flush 할 때 이동한다.
    initDeepLinks();
    hideKeyboardAccessoryBar();

    // Android 하드웨어 뒤로가기: 헤더바 뒤로가기와 동일하게 history를 따라가고,
    // 더 갈 곳이 없으면(루트) 앱을 종료한다.
    CapacitorApp.addListener('backButton', ({ canGoBack }) => {
        if (canGoBack) {
            window.history.back();
        } else {
            CapacitorApp.exitApp();
        }
    });
}

/**
 * iOS 네이티브 앱 여부.
 * App Store Guideline 4.8 대응: 서드파티 SSO(카카오)를 제공하면 Sign in with Apple을 함께 제공해야 하므로,
 * Sign in with Apple을 붙이기 전까지 iOS 앱에서만 카카오 로그인/가입 버튼을 숨기는 데 사용한다.
 * 웹·Android에는 영향 없음.
 */
export function isIosApp(): boolean {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
}

// ───────── 푸시 알림 ─────────

/** 이 기기에서 서버에 등록한 FCM 토큰 (로그아웃 시 해제용). 기기 로컬에만 보관. */
const PUSH_TOKEN_STORAGE_KEY = 'mc_push_token';
let pushListenersAttached = false;

function readStoredToken(): string | null {
    try {
        return localStorage.getItem(PUSH_TOKEN_STORAGE_KEY);
    } catch {
        return null;
    }
}

function storeToken(token: string | null): void {
    try {
        if (token) localStorage.setItem(PUSH_TOKEN_STORAGE_KEY, token);
        else localStorage.removeItem(PUSH_TOKEN_STORAGE_KEY);
    } catch {
        // 저장 실패는 무시 — 다음 실행 때 다시 등록된다
    }
}

/** 푸시 페이로드의 link(절대 URL 또는 경로)를 앱 내 라우팅으로 연다. 외부 도메인은 무시. */
function openPushLink(link: unknown): void {
    if (typeof link !== 'string' || !link) return;
    try {
        const url = new URL(link, window.location.origin);
        if (url.origin !== window.location.origin) return;
        // 결제 결과 페이지(토스 복귀)에서 다른 화면으로 옮기면 서버 승인 전에 페이지가 사라져 결제가 승인되지 않는다.
        // 문서가 다시 로드될 때 예전 알림 탭 이벤트가 다시 전달되는 경우까지 막는다.
        if (/\/payment\/(success|fail)(\/|$)/.test(window.location.pathname)) return;
        markPushEntry(); // 옮긴 화면의 상세 보기 출처를 push 로
        navigationService.navigateTo(`${url.pathname}${url.search}`);
    } catch {
        // 잘못된 링크는 무시
    }
}

/**
 * 푸시 알림 등록 — 로그인된 상태에서 호출한다(앱 실행마다 호출해도 안전).
 *
 * 1. OS 알림 권한 요청(최초 1회 팝업, 이후엔 기존 결정 반환)
 * 2. FCM 토큰 발급(iOS는 AppDelegate에서 APNs→FCM 변환) → 서버 PUT /users/me/devices
 * 3. 알림 탭 시 payload.link 로 앱 내 이동
 *
 * 웹 브라우저에서는 아무것도 하지 않는다.
 */
export async function registerPushNotifications(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
        return;
    }

    if (!pushListenersAttached) {
        pushListenersAttached = true;

        await PushNotifications.addListener('registration', async (token) => {
            storeToken(token.value);
            try {
                const info = await CapacitorApp.getInfo();
                await api.user.registerDevice({
                    platform: Capacitor.getPlatform() === 'ios' ? 'IOS' : 'ANDROID',
                    token: token.value,
                    appVersion: `${info.version} (${info.build})`,
                });
            } catch (error) {
                console.error('push token 등록 실패:', error);
            }
        });

        await PushNotifications.addListener('registrationError', (error) => {
            console.error('push 등록 오류:', error);
        });

        // 앱이 포그라운드일 때 도착한 알림은 OS가 배너를 띄운다(capacitor.config presentationOptions).
        // 사용자가 알림을 탭했을 때만 링크로 이동한다.
        await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
            const data = action.notification.data ?? {};
            // 열람 집계 — 실패해도 이동은 진행 (fire-and-forget)
            if (typeof data.logId === 'string' && data.logId) {
                api.user.reportPushOpen(data.logId).catch((error) => console.error('push 열람 보고 실패:', error));
            }
            openPushLink(data.link);
        });
    }

    const permission = await PushNotifications.requestPermissions();
    if (permission.receive !== 'granted') {
        return;
    }

    await PushNotifications.register();
}

/** 네이티브 앱(Capacitor) 안인지 — 웹 브라우저면 false */
export function isNativeApp(): boolean {
    return Capacitor.isNativePlatform();
}

/**
 * AI 대화에서 알림을 켠 뒤 이 기기에서 푸시를 받게 한다 — 앱이면 알림 허용 창(처음 한 번)을 띄우고 토큰을 등록한다.
 * 이미 거부했으면 창이 다시 뜨지 않으므로 'denied'(휴대폰 설정 안내), 웹이면 'web'(앱 안내).
 */
export async function ensurePushPermission(): Promise<'granted' | 'denied' | 'web'> {
    if (!Capacitor.isNativePlatform()) return 'web';
    try {
        const current = await PushNotifications.checkPermissions();
        if (current.receive === 'denied') return 'denied';
        await registerPushNotifications();
        const after = await PushNotifications.checkPermissions();
        return after.receive === 'granted' ? 'granted' : 'denied';
    } catch (error) {
        console.error('푸시 권한 확인 실패:', error);
        return 'denied';
    }
}

/** 로그아웃 시 이 기기의 토큰을 서버에서 해제한다. 웹 브라우저·미등록 기기면 아무것도 안 한다. */
export async function unregisterPushDevice(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
        return;
    }
    const token = readStoredToken();
    if (!token) return;
    try {
        await api.user.unregisterDevice(token);
    } catch (error) {
        console.error('push token 해제 실패:', error);
    } finally {
        storeToken(null);
    }
}

/**
 * iOS 키보드 위의 입력 도구 줄(∧ ∨ 완료)을 숨긴다 — 댓글 입력칸이 키보드에 바로 붙게(2026-10-03 사용자 지시 "키보드는 입력창이 고정된 느낌").
 * 이 줄이 키보드와 입력 시트 사이를 벌렸다. 키보드 플러그인이 들어간 앱 버전에서만 동작하고, 옛 앱(플러그인 없음)은 그대로 둔다.
 */
function hideKeyboardAccessoryBar(): void {
    if (Capacitor.getPlatform() !== 'ios' || !Capacitor.isPluginAvailable('Keyboard')) return;
    Keyboard.setAccessoryBarVisible({ isVisible: false }).catch(() => undefined);
}
